import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { QueryParamsHandling, RouterLink } from '@angular/router';
import { NamespacedIconName } from 'src/app/shared/constants/material-icon-name';

export interface DsBreadcrumbItem {
  label: string;
  link?: string | unknown[];
  icon?: NamespacedIconName;
  disabled?: boolean;
}

@Component({
  selector: 'ds-breadcrumb',
  standalone: true,
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Một dòng, KHÔNG xuống dòng (01/10/2026): breadcrumb sống trong topbar cao 48px. Khi topbar
  // thêm nút tỷ giá, ở 1024px bản `flex-wrap` bẻ breadcrumb thành ba dòng tràn ra ngoài thanh
  // (đo trên ảnh CI `order-hub--ready--tablet`). Các mục có `min-w-0` + `truncate` sẵn nên hết
  // chỗ thì cắt chữ thay vì vỡ dòng.
  styles: [':host { display: block; }'],
  template: `
    <nav [attr.aria-label]="ariaLabel || 'Breadcrumb'" class="min-w-0">
      <ol
        class="flex items-center flex-nowrap min-w-0 overflow-hidden gap-1 text-token-body list-none p-0 m-0"
      >
        @for (item of visibleItems; track $index; let last = $last; let first = $first) {
          @if (item === collapsedPlaceholder) {
            <li class="flex items-center text-warm-hint">
              <button
                type="button"
                class="px-2 py-0.5 rounded hover:bg-warm-hover text-token-caption font-semibold border-0 bg-transparent cursor-pointer focus-visible:outline-none focus-visible:shadow-focus"
                (click)="onExpand()"
                aria-label="Show hidden breadcrumb items"
              >
                …
              </button>
              <span class="material-symbols-outlined icon-size-token-2xs text-muted-soft mx-0.5">
                {{ separatorIcon }}
              </span>
            </li>
          } @else {
            <li class="flex items-center min-w-0">
              @if (!first) {
                <span class="material-symbols-outlined icon-size-token-2xs text-muted-soft mx-0.5">
                  {{ separatorIcon }}
                </span>
              }
              @if (item.link && !last && !item.disabled) {
                <a
                  [routerLink]="item.link"
                  [queryParamsHandling]="queryParamsHandling"
                  [attr.title]="item.label"
                  class="inline-flex items-center gap-1 text-warm-hint hover:text-warm-text transition-colors truncate"
                >
                  @if (item.icon) {
                    <span class="material-symbols-outlined icon-size-token-xs">
                      {{ item.icon }}
                    </span>
                  }
                  <span class="truncate">{{ item.label }}</span>
                </a>
              } @else {
                <span
                  class="inline-flex items-center gap-1 truncate"
                  [class.text-warm-text]="last"
                  [class.font-medium]="last"
                  [class.text-warm-hint]="!last"
                  [attr.aria-current]="last ? 'page' : null"
                  [attr.title]="item.label"
                >
                  @if (item.icon) {
                    <span class="material-symbols-outlined icon-size-token-xs">
                      {{ item.icon }}
                    </span>
                  }
                  <span class="truncate">{{ item.label }}</span>
                </span>
              }
            </li>
          }
        }
      </ol>
    </nav>
  `,
})
export class DsBreadcrumbComponent {
  @Input() items: DsBreadcrumbItem[] = [];
  @Input() maxVisible = 4;
  @Input() separatorIcon = 'chevron_right';
  @Input() ariaLabel = '';

  /**
   * Chuyển thẳng xuống `routerLink` của từng mục.
   *
   * Thanh điều hướng trên cùng chạy `preserve`: bối cảnh tổ chức (tenant / đơn vị / cơ sở)
   * đi theo query param, nên một liên kết breadcrumb bỏ query sẽ ném người dùng về bối cảnh
   * mặc định mà không báo gì. Mặc định `''` giữ nguyên hành vi cũ cho mọi nơi gọi khác.
   */
  @Input() queryParamsHandling: QueryParamsHandling = '';

  @Output() expand = new EventEmitter<void>();

  readonly collapsedPlaceholder: DsBreadcrumbItem = { label: '…' };

  expanded = false;

  get visibleItems(): DsBreadcrumbItem[] {
    if (this.expanded || this.items.length <= this.maxVisible) return this.items;
    const first = this.items[0];
    const tail = this.items.slice(-(this.maxVisible - 2));
    return [first, this.collapsedPlaceholder, ...tail];
  }

  onExpand(): void {
    this.expanded = true;
    this.expand.emit();
  }
}
